"use client";

import { X } from "lucide-react";
import type * as React from "react";
import { cn } from "@/lib/utils";

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: "stroke" | "gray";
  disabled?: boolean;
  onDismiss?: () => void;
  /** Accessible name for the dismiss button, e.g. "Remove Healthcare". Defaults to "Remove". */
  dismissLabel?: string;
  icon?: React.ReactNode;
}

export function Tag({
  className,
  variant = "stroke",
  disabled = false,
  onDismiss,
  dismissLabel,
  icon,
  children,
  ...props
}: TagProps) {
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-md pl-2 pr-1 text-xs font-medium",
        variant === "stroke" && "border border-border bg-background text-foreground",
        variant === "gray" && "bg-muted text-muted-foreground",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      {...props}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      {children}
      {onDismiss && !disabled && (
        <button
          type="button"
          onClick={onDismiss}
          className="ml-0.5 shrink-0 rounded p-px opacity-60 hover:opacity-100 transition-opacity duration-[var(--duration-fast)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={dismissLabel ?? "Remove"}
        >
          <X className="size-3.5" />
        </button>
      )}
    </span>
  );
}
